import { useCallback, useEffect, useMemo, useState } from "react";
import { apiClient, authStore, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { formatINR, formatCompactINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { LogOut, Phone, Trash2 } from "lucide-react";
import { toast } from "sonner";

import LeadsFilterBar from "@/components/admin/LeadsFilterBar";
import ChangePasswordDialog from "@/components/admin/ChangePasswordDialog";
import UsersDialog from "@/components/admin/UsersDialog";

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</div>
      <div
        className={`mt-1 font-heading text-2xl font-semibold num-font ${
          accent ? "text-[#F97316]" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function useLeadsData({ q, dateFrom, dateTo, statusFilter }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (statusFilter === "pending") params.contacted = false;
      if (statusFilter === "contacted") params.contacted = true;
      const { data } = await apiClient.get("/admin/leads", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load leads"));
    } finally {
      setLoading(false);
    }
  }, [q, dateFrom, dateTo, statusFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  return { loading, items, setItems, total, setTotal, fetchLeads };
}

function LeadsTable({ items, loading, canDelete, onToggle, onDelete }) {
  const colSpan = canDelete ? 9 : 8;
  return (
    <div className="rounded-2xl border border-white/10 bg-[#111827]/70 overflow-hidden">
      <div className="overflow-x-auto">
        <Table data-testid="leads-table">
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Captured</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Name</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Mobile</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Age / Freedom</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400 text-right">Expenses</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400 text-right">Target Corpus</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400 text-right">Step-up SIP</TableHead>
              <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Contacted</TableHead>
              {canDelete && (
                <TableHead className="text-[11px] uppercase tracking-[0.18em] text-slate-400 text-right">Actions</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((l) => (
              <TableRow
                key={l.id}
                className="border-white/5 hover:bg-white/5 transition-colors"
                data-testid={`lead-row-${l.id}`}
              >
                <TableCell className="text-xs text-slate-400 num-font whitespace-nowrap">
                  {fmtDateTime(l.created_at)}
                </TableCell>
                <TableCell className="font-medium text-white">{l.first_name}</TableCell>
                <TableCell className="num-font text-slate-200">
                  <a
                    href={`tel:${l.mobile}`}
                    className="inline-flex items-center gap-1.5 hover:text-[#F97316]"
                  >
                    <Phone className="w-3 h-3 text-[#F97316]" />
                    +91 {l.mobile}
                  </a>
                </TableCell>
                <TableCell className="num-font text-slate-300 whitespace-nowrap">
                  {l.current_age} → {l.freedom_age}
                </TableCell>
                <TableCell className="num-font text-right text-slate-300">
                  {formatINR(l.monthly_expenses)}
                </TableCell>
                <TableCell className="num-font text-right text-white font-medium">
                  {formatCompactINR(l.target_corpus)}
                </TableCell>
                <TableCell className="num-font text-right text-slate-300">
                  {formatINR(l.stepup_sip)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={!!l.contacted}
                      onCheckedChange={() => onToggle(l)}
                      data-testid={`lead-contacted-${l.id}`}
                    />
                    {l.contacted ? (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-400"
                        data-testid={`lead-status-badge-${l.id}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                        Contacted
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium text-amber-400"
                        data-testid={`lead-status-badge-${l.id}`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                        Pending
                      </span>
                    )}
                  </div>
                </TableCell>
                {canDelete && (
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(l)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      data-testid={`lead-delete-${l.id}`}
                      title="Delete lead"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow className="border-white/5">
                <TableCell colSpan={colSpan} className="text-center text-sm text-slate-500 py-12">
                  No leads match these filters yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default function AdminLeadsPage() {
  const { user, logout } = useAuth();
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { loading, items, setItems, total, setTotal, fetchLeads } = useLeadsData({
    q, dateFrom, dateTo, statusFilter,
  });

  const canDelete = user?.role === "admin";

  const toggleContacted = async (lead) => {
    try {
      const { data } = await apiClient.patch(`/admin/leads/${lead.id}`, {
        contacted: !lead.contacted,
      });
      setItems((prev) => prev.map((l) => (l.id === data.id ? { ...l, ...data } : l)));
      toast.success(data.contacted ? "Marked as contacted" : "Marked as open");
    } catch (err) {
      toast.error(formatApiError(err, "Could not update lead"));
    }
  };

  const deleteLead = async (lead) => {
    const label = lead.first_name || lead.mobile || "this lead";
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/admin/leads/${lead.id}`);
      setItems((prev) => prev.filter((l) => l.id !== lead.id));
      setTotal((t) => Math.max(0, t - 1));
      toast.success(`Deleted ${label}`);
    } catch (err) {
      toast.error(formatApiError(err, "Could not delete lead"));
    }
  };

  const deleteAllFiltered = async () => {
    if (items.length === 0) {
      toast.info("No leads to delete");
      return;
    }
    const msg =
      total > items.length
        ? `Delete all ${total} leads matching the current filters? This cannot be undone.`
        : `Delete all ${items.length} leads shown? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      const params = { confirm: true };
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (statusFilter === "pending") params.contacted = false;
      if (statusFilter === "contacted") params.contacted = true;
      const { data } = await apiClient.delete("/admin/leads", { params });
      toast.success(`Deleted ${data.count} lead${data.count === 1 ? "" : "s"}`);
      fetchLeads();
    } catch (err) {
      toast.error(formatApiError(err, "Could not delete leads"));
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (statusFilter === "pending") params.set("contacted", "false");
      if (statusFilter === "contacted") params.set("contacted", "true");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/leads.csv?${params.toString()}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${authStore.getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `sahi-disha-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("CSV downloaded");
    } catch (_err) {
      toast.error("Could not export CSV");
    }
  };

  const clearFilters = () => {
    setQ("");
    setDateFrom("");
    setDateTo("");
    setStatusFilter("all");
  };

  const summary = useMemo(() => {
    const contacted = items.filter((i) => i.contacted).length;
    const pending = items.length - contacted;
    const totalCorpus = items.reduce((s, i) => s + (i.target_corpus || 0), 0);
    return { contacted, pending, totalCorpus };
  }, [items]);

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white" data-testid="admin-leads-page">
      <header className="sticky top-0 z-40 bg-[#0A0F1C]/85 backdrop-blur-md border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#F97316]">
              Sahi Disha
            </div>
            <div className="font-heading font-semibold tracking-tight">
              Leads Dashboard
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-300">
              <span className="text-slate-500">Signed in as</span>
              <span className="font-medium">{user?.username}</span>
              <Badge
                variant="outline"
                className="border-[#F97316]/40 text-[#FDBA74] bg-[#F97316]/10"
              >
                {user?.role}
              </Badge>
            </div>
            {user?.role === "admin" && <UsersDialog />}
            <ChangePasswordDialog />
            <Button
              variant="outline"
              onClick={logout}
              className="border-white/15 bg-transparent hover:bg-white/5 text-white"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Total matching" value={total} accent />
          <StatCard label="Contacted" value={summary.contacted} />
          <StatCard label="Pending" value={summary.pending} />
          <StatCard
            label="Total projected corpus"
            value={formatCompactINR(summary.totalCorpus)}
          />
        </div>

        <div className="mt-8 rounded-2xl border border-white/10 bg-[#111827]/70 p-4 sm:p-6">
          <LeadsFilterBar
            q={q}
            setQ={setQ}
            dateFrom={dateFrom}
            setDateFrom={setDateFrom}
            dateTo={dateTo}
            setDateTo={setDateTo}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            onRefresh={fetchLeads}
            onClear={clearFilters}
            onExport={exportCsv}
            onDeleteAll={deleteAllFiltered}
            canDelete={canDelete}
            loading={loading}
            itemsCount={items.length}
            total={total}
          />
        </div>

        <div className="mt-6">
          <LeadsTable
            items={items}
            loading={loading}
            canDelete={canDelete}
            onToggle={toggleContacted}
            onDelete={deleteLead}
          />
        </div>
      </main>
    </div>
  );
}
